/**
 * Admin auth client — our own magic-link flow against vv-api + D1
 * (replaced Supabase GoTrue in the 2026-06 D1 migration).
 *
 * Flow (sign-in codes ported from the Hype Forge Base, 2026-10-21):
 *   1. requestLink(email)  → API emails an 8-digit code and a single-use
 *      link to /auth/callback#token=… (the token rides in the fragment).
 *   2. verifyCode(email, code) on the login page, or verifyToken(token) on
 *      the link's callback page → API returns a 30-day JWT + user; we
 *      persist both in localStorage and notify subscribers.
 *   3. getSession()        → { access_token, user } | null (expiry-checked)
 *   4. signOut()           → clears storage, notifies.
 *
 * Too many sign-in attempts (HTTP 429; docs/sign-in.md): requestLink,
 * verifyCode and verifyToken hand back `retryAfter` (seconds) with the wait
 * in words as `error`. The calls themselves live in lib/signIn.ts (no Vite
 * globals, so node:test covers them). With staff Turnstile on, requestLink
 * and verifyCode take the login page's widget handle and send its token
 * (action staff_login, then staff_verify for the code; 2026-10-22).
 *
 * The Session shape intentionally mirrors what call sites used from
 * supabase-js (`session.access_token`, `session.user.email`) so the
 * pages didn't have to change when the backend moved.
 */

import { checkSignInCode, DEFAULT_CODE_LENGTH, redeemSignIn, sendSignInCode, type LoginTurnstile, type SignInSession } from './signIn';

export { DEFAULT_CODE_LENGTH };

const API_BASE = import.meta.env.VITE_API_URL ?? 'https://api.valleyvitalitywellness.com';

const TOKEN_KEY = 'vv_admin_token';
const USER_KEY = 'vv_admin_user';
const EXPIRES_KEY = 'vv_admin_expires';

export interface AuthUser {
  id: string;
  email: string;
  role: 'dev' | 'admin' | 'editor';
}

export interface Session {
  access_token: string;
  expires_at: string;
  user: AuthUser;
}

type Listener = (session: Session | null) => void;
const listeners = new Set<Listener>();

function notify(session: Session | null) {
  for (const fn of listeners) fn(session);
}

export function getSession(): Session | null {
  try {
    const token = localStorage.getItem(TOKEN_KEY);
    const userRaw = localStorage.getItem(USER_KEY);
    const expiresAt = localStorage.getItem(EXPIRES_KEY);
    if (!token || !userRaw || !expiresAt) return null;
    if (new Date(expiresAt).getTime() <= Date.now()) {
      clearSession();
      return null;
    }
    return { access_token: token, expires_at: expiresAt, user: JSON.parse(userRaw) as AuthUser };
  } catch {
    return null;
  }
}

function storeSession(s: Session) {
  localStorage.setItem(TOKEN_KEY, s.access_token);
  localStorage.setItem(USER_KEY, JSON.stringify(s.user));
  localStorage.setItem(EXPIRES_KEY, s.expires_at);
  notify(s);
}

function clearSession() {
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(USER_KEY);
  localStorage.removeItem(EXPIRES_KEY);
}

/**
 * Ask for a sign-in email (an 8-digit code plus a link). `website` is the
 * login form's honeypot field: empty for people, so the API sends the email.
 * `turnstile` is the login page's Turnstile widget (null when staff Turnstile
 * is off): its token goes with the request, and the widget is then readied
 * for the code step (lib/signIn.ts). The API says how many digits the code
 * has. Too many sign-in attempts (HTTP 429): `retryAfter` is the wait in
 * seconds and `error` says it in words (lib/signIn.ts, tested there).
 */
export function requestLink(
  email: string,
  website = '',
  turnstile: LoginTurnstile | null = null,
): Promise<{ error: string | null; codeLength: number; retryAfter?: number }> {
  return sendSignInCode(API_BASE, email, website, turnstile);
}

type Redeemed = { error: string | null; retryAfter?: number; turnstileFailed?: true; session?: SignInSession };

/** Store the session a link or a code was traded for. One path for both, so
 * what happens after sign-in does not depend on which was used. */
function keep({ error, retryAfter, turnstileFailed, session }: Redeemed): { error: string | null; retryAfter?: number; turnstileFailed?: true } {
  if (session) {
    storeSession(session);
    return { error: null };
  }
  if (retryAfter) return { error, retryAfter };
  return turnstileFailed ? { error, turnstileFailed } : { error };
}

/**
 * The single-use token of an emailed sign-in link. New links carry it in the
 * URL FRAGMENT, `/auth/callback#token=…`: a fragment never leaves the
 * browser, so the token is not in the admin host's request logs, a Referer or
 * a link-scanning proxy. A link emailed before 2026-10-21 (`?token=…`) still
 * works until it expires.
 */
export function linkTokenFrom(hash: string, search: string): string | null {
  const fromHash = new URLSearchParams(hash.replace(/^#/, '')).get('token');
  if (fromHash) return fromHash;
  return new URLSearchParams(search.replace(/^\?/, '')).get('token');
}

/** The emailed link: no Turnstile (the link itself is the proof). */
export async function verifyToken(token: string): Promise<{ error: string | null; retryAfter?: number }> {
  return keep(await redeemSignIn(API_BASE, { token }, 'Verify failed'));
}

/**
 * The emailed code: signs in THIS browser (the link signs in whichever one the mail app opens). With staff Turnstile
 * on, a token for the code step goes with it and the widget is reset after every attempt; `turnstileFailed` means that
 * check failed (no try was spent, so the page keeps the typed code).
 */
export async function verifyCode(
  email: string,
  code: string,
  turnstile: LoginTurnstile | null = null,
): Promise<{ error: string | null; retryAfter?: number; turnstileFailed?: true }> {
  return keep(await checkSignInCode(API_BASE, email, code, turnstile));
}

export function signOut(): void {
  clearSession();
  notify(null);
}

/** Standard authed-fetch headers — bearer token, plus JSON content type
 * unless `json` is false (GETs). */
export function authHeaders(json = true): Record<string, string> {
  const session = getSession();
  return {
    ...(json ? { 'Content-Type': 'application/json' } : {}),
    ...(session ? { Authorization: `Bearer ${session.access_token}` } : {}),
  };
}

/** Subscribe to session changes. Returns an unsubscribe function. */
export function onAuthChange(fn: Listener): () => void {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

/**
 * Validate the stored token against the API on boot — catches revoked
 * access and picks up role changes. Cheap (one indexed D1 read).
 */
export async function refreshUser(): Promise<void> {
  const session = getSession();
  if (!session) return;
  try {
    const res = await fetch(`${API_BASE}/api/auth/me`, {
      headers: { Authorization: `Bearer ${session.access_token}` },
    });
    if (res.status === 401 || res.status === 403) {
      signOut();
      return;
    }
    if (res.ok) {
      const body = (await res.json()) as { user?: AuthUser };
      if (body.user) {
        storeSession({ ...session, user: body.user });
      }
    }
  } catch {
    // Network hiccup — keep the local session; API calls will surface
    // real auth failures.
  }
}
